import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { scoreLabel, USE_CASES } from "@/lib/constants";
import { useSeo } from "@/hooks/useSeo";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";

export default function Listings() {
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const useCase = params.get("use_case");
  const city = params.get("city");
  const useCaseLabel = USE_CASES.find((u) => u.value === useCase)?.label;
  const seoTitle = useCaseLabel
    ? `Annonces ${useCaseLabel}${city ? ` à ${city}` : ""}`
    : city ? `Annonces sous-sols à ${city}` : "Toutes les annonces de sous-sols";
  const seoDesc = useCaseLabel
    ? `Trouvez un sous-sol compatible ${useCaseLabel.toLowerCase()}${city ? ` à ${city}` : " en France"}. Filtres par surface, hauteur, ERP, ventilation et puissance électrique. Sous-sols, caves, parkings, réserves.`
    : "Explorez tous les sous-sols disponibles sur R-1.immo. Filtrez par ville, surface, prix, hauteur, statut ERP et usage compatible.";
  useSeo({
    title: seoTitle,
    description: seoDesc,
    canonical: useCase || city ? "https://r-1.immo/annonces" : undefined,
  });

  const [filters, setFilters] = useState({
    city: params.get("city") || "",
    min_surface: params.get("min_surface") || "",
    max_price: params.get("max_price") || "",
    min_height: params.get("min_height") || "",
    erp: params.get("erp") === "true",
    access_independent: params.get("access_independent") === "true",
    delivery: params.get("delivery") === "true",
    ventilation: params.get("ventilation") === "true",
    access_247: params.get("access_247") === "true",
    freight_elevator: params.get("freight_elevator") === "true",
    min_power: params.get("min_power") || "",
    use_case: params.get("use_case") || "",
  });

  const fetch = async () => {
    setLoading(true);
    const q = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false || v == null) return;
      q[k] = v;
    });
    try {
      const { data } = await api.get("/listings", { params: q });
      setListings(data);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, [filters]);// eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    const sp = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => {
      if (v === "" || v === false || v == null) return;
      sp.set(k, String(v));
    });
    setParams(sp, { replace: true });
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12">
      <div className="mb-12">
        <div className="label-mono mb-3 text-amber-500">catalogue</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">Toutes les annonces</h1>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* FILTERS */}
        <aside className="border border-zinc-800 bg-zinc-950 p-5 h-fit lg:sticky lg:top-24 space-y-6" data-testid="filters-panel">
          <div>
            <div className="label-mono mb-2">Ville / Quartier</div>
            <input
              data-testid="filter-city"
              value={filters.city}
              onChange={(e) => update({ city: e.target.value })}
              placeholder="Paris, Lyon…"
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
            />
          </div>
          <div>
            <div className="label-mono mb-2">Usage recherché</div>
            <select
              data-testid="filter-usecase"
              value={filters.use_case}
              onChange={(e) => update({ use_case: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
            >
              <option value="">Tous</option>
              {USE_CASES.map((u) => (<option key={u.value} value={u.value}>{u.label}</option>))}
            </select>
          </div>
          {[
            { k: "min_surface", label: "Surface min (m²)", type: "number" },
            { k: "max_price", label: "Loyer max (€)", type: "number" },
            { k: "min_height", label: "Hauteur min (m)", type: "number", step: "0.1" },
            { k: "min_power", label: "Puissance min (kVA)", type: "number" },
          ].map((f) => (
            <div key={f.k}>
              <div className="label-mono mb-2">{f.label}</div>
              <input
                data-testid={`filter-${f.k}`}
                type={f.type}
                step={f.step}
                value={filters[f.k]}
                onChange={(e) => update({ [f.k]: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
              />
            </div>
          ))}

          <div className="border-t border-zinc-800 pt-4 space-y-3">
            {[
              { k: "erp", label: "Statut ERP" },
              { k: "access_independent", label: "Accès indépendant" },
              { k: "access_247", label: "Accès 24/7" },
              { k: "delivery", label: "Livraison possible" },
              { k: "ventilation", label: "Ventilation" },
              { k: "freight_elevator", label: "Monte-charge" },
            ].map((c) => (
              <label key={c.k} className="flex items-center gap-3 text-sm cursor-pointer">
                <Checkbox
                  data-testid={`filter-${c.k}`}
                  checked={filters[c.k]}
                  onCheckedChange={(v) => update({ [c.k]: !!v })}
                />
                <span className="text-zinc-300">{c.label}</span>
              </label>
            ))}
          </div>

          <button
            data-testid="clear-filters-btn"
            onClick={() => setFilters({ city: "", min_surface: "", max_price: "", min_height: "", erp: false, access_independent: false, delivery: false, ventilation: false, access_247: false, freight_elevator: false, min_power: "", use_case: "" })}
            className="w-full border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 py-2 text-xs uppercase tracking-widest"
          >
            Réinitialiser
          </button>
        </aside>

        {/* RESULTS */}
        <div>
          <div className="label-mono mb-4 text-zinc-500">
            {loading ? "CHARGEMENT..." : `${listings.length} résultat${listings.length > 1 ? "s" : ""}`}
          </div>
          {listings.length === 0 && !loading ? (
            <div className="border border-zinc-800 p-16 text-center">
              <div className="label-mono mb-4">Aucune annonce ne correspond aux filtres</div>
              <button onClick={() => update({ city: "", use_case: "" })} className="text-amber-500 text-sm underline">Effacer les filtres</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
              {listings.map((l) => <ListingCard key={l.id} l={l} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatPrice(l) {
  if (l.monthly_rent > 0) return { amount: `${l.monthly_rent}€`, suffix: "/mois" };
  if (l.sale_price > 0) return { amount: `${l.sale_price}€`, suffix: " vente" };
  return { amount: "—", suffix: "" };
}

function ListingCard({ l }) {
  const s = scoreLabel(l.score || 0);
  const price = formatPrice(l);
  return (
    <Link
      to={`/annonces/${l.id}`}
      data-testid={`listing-card-${l.id}`}
      className="group block bg-zinc-950 hover:bg-zinc-900 transition-colors"
    >
      <div className="aspect-[4/3] bg-zinc-900 overflow-hidden relative">
        {l.photos?.[0] ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${l.photos[0]}`} alt={l.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center label-mono text-zinc-700">[ SANS · IMAGE ]</div>
        )}
        <div className="absolute top-3 left-3 label-mono bg-zinc-950/80 backdrop-blur-sm px-2 py-1">{l.city}</div>
        <div className={`absolute top-3 right-3 font-mono-tech text-xs px-2 py-1 bg-zinc-950/80 ${s.color}`}>[ {l.score || 0} / 100 ]</div>
      </div>
      <div className="p-5">
        <div className="font-display text-xl leading-tight mb-2 group-hover:text-amber-500 transition-colors">{l.title}</div>
        <div className="flex flex-wrap gap-x-3 label-mono text-zinc-500 mb-3">
          <span>{l.surface_exploitable || l.surface_total}m²</span>
          <span>·</span>
          <span>H{l.ceiling_height_min}m</span>
          {l.is_erp && <><span>·</span><span className="text-amber-500">ERP</span></>}
        </div>
        <div className="flex items-center justify-between border-t border-zinc-800 pt-3">
          <div className="font-display text-lg">
            {price.amount}
            <span className="label-mono ml-1">{price.suffix}</span>
          </div>
          <div className={`text-xs label-mono ${s.color}`}>{s.label}</div>
        </div>
      </div>
    </Link>
  );
}
